// Ported from dev-scrips-pm-react src/billing/lib/member-discount.ts (PROD-3312 appointment Billing tab).
/**
 * The plan-level member discount, as the patient is told about it (PROD-2523).
 *
 * PROD-2516 added `Package.DiscountPercent` — a blanket percentage on a
 * membership plan. It prices correctly and has done since it shipped, but the
 * only screen that ever mentioned it was the sheet the plan was authored on,
 * so a member asking "where is my 10%?" could not be answered anywhere.
 *
 * ## The ladder these helpers have to stay true to
 *
 * ```
 * 1. allowance remaining   -> covered, patient pays 0
 * 2. explicit MemberPrice  -> that price          (per service)
 * 3. plan DiscountPercent  -> list price less N%  (per plan)
 * 4. otherwise             -> list price
 * ```
 *
 * **Rungs 2 and 3 never stack.** An explicit member price wins outright, even
 * when the percentage would have been cheaper (`CalculatorService.ApplyMemberPrices`
 * takes the member price and `continue`s). Every string minted here therefore
 * says what the percentage applies TO, never a bare "10% off everything" — that
 * is a promise the pricing code does not keep.
 *
 * ## Which rung priced an invoice line
 *
 * Billing sets `MemberPriceApplied` and `ListFeeBeforeMemberPrice` on rung 2
 * and rung 3 identically. Since PROD-2525 it also records
 * `MemberDiscountPercentApplied` — the plan percentage — on rung-3 lines only,
 * and the invoice table reads that to say "Member discount 10%" rather than
 * "Member price". Lines priced before that column carry null whichever rung
 * priced them; for those, {@link effectiveDiscountPercent} turns the recorded
 * pair into the percentage the saving works out to, WITHOUT claiming which
 * rung produced it.
 */

/**
 * A plan percentage as text: `10` → `"10"`, `12.5` → `"12.5"`, `7.25` → `"7.25"`.
 *
 * The column is `DECIMAL(5,2)`, so two decimals is the full precision; trailing
 * zeros are dropped because "10% off" is what a clinic advertises and "10.00%"
 * reads like a computed figure rather than the round number someone typed.
 */
export function formatDiscountPercent(percent: number): string {
  return String(Number(percent.toFixed(2)));
}

/**
 * Is this plan percentage worth telling anyone about?
 *
 * Mirrors `MemberPricing.HasDiscountPercent` exactly — `HasValue && > 0`. The
 * distinction matters: `null` means the plan confers no percentage and `0`
 * means someone authored a 0% one, and while those read back differently on the
 * authoring sheet, **neither takes anything off a price**. A card claiming "0%
 * off services with no member price" would be announcing a benefit that is not
 * one. Negative cannot be authored (`CK_Package_DiscountPercent`) and is
 * rejected here for the same reason the calculator clamps it.
 */
export function hasMemberDiscount(percent?: number | null): percent is number {
  return typeof percent === 'number' && Number.isFinite(percent) && percent > 0;
}

/**
 * What the member gets, in one line, for the membership card.
 *
 * `"10% off services with no member price"` — deliberately not "10% off
 * treatments", which is how clinics advertise it and how the ladder does NOT
 * work. The qualifier is the whole honesty of the sentence: it is also what
 * makes the blank member-price column on the card's service rows legible, since
 * those blanks are exactly the rows the percentage covers.
 *
 * Returns null when there is no percentage to state, so the caller renders
 * nothing rather than an empty chip.
 */
export function memberDiscountLabel(percent?: number | null): string | null {
  if (!hasMemberDiscount(percent)) return null;
  return `${formatDiscountPercent(percent)}% off services with no member price`;
}

/**
 * The member-price column's entry for a service the plan sets no price for:
 * `"−10%"`, with a true minus sign (U+2212) rather than a hyphen, because the
 * column is `tabular-nums` and a hyphen sits at a different height to the digits
 * beside it.
 *
 * This is the ladder rendered: a row showing `AED 150.00` is rung 2, a row
 * showing `−10%` is rung 3, and one showing neither is on neither rung. It is
 * the only per-row marking either surface gains, and it earns its place by
 * completing a column that was otherwise blank on exactly the rows the patient
 * is asking about.
 */
export function memberDiscountColumnText(percent?: number | null): string | null {
  if (!hasMemberDiscount(percent)) return null;
  return `−${formatDiscountPercent(percent)}%`;
}

/**
 * What percentage a membership actually took off ONE invoice line, from the two
 * numbers Billing recorded on it at pricing time.
 *
 * `saved` is the whole-line saving and `listTotal` the list fee for the quantity
 * billed, so this is line-scoped arithmetic on line-scoped inputs — no lookup
 * against the patient's current plan. That is the point: the plan is read live
 * and may have been repriced since the invoice was priced, so deriving anything
 * from it would make an old invoice report today's benefit.
 *
 * **It does not identify the rung.** A per-service member price of 270 against a
 * 300 list fee also returns 10. The number is a true statement about this line
 * either way. Billing now records the rung (see the module note); this is the
 * fallback for lines priced before it did.
 *
 * Rounded to one decimal so the backend's own 2dp rounding does not surface as
 * `9.9991`: a 10% cut of 333.33 is charged at 300.00, and the member is owed the
 * answer "10%", not a rounding artefact. Null when the inputs cannot support a
 * percentage at all (no list total, nothing saved), or when the result rounds to
 * 0 — "0% off" is noise, not an explanation.
 */
export function effectiveDiscountPercent(saved: number, listTotal: number): number | null {
  if (!Number.isFinite(saved) || !Number.isFinite(listTotal)) return null;
  if (listTotal <= 0 || saved <= 0) return null;
  const pct = Math.round((saved / listTotal) * 1000) / 10;
  return pct > 0 ? pct : null;
}
